import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon, UsersIcon, CheckBadgeIcon, ShieldCheckIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useModal } from '../hooks/useModal';
import { useTableQuery } from '../hooks/useTableQuery';
import { useCrud } from '../hooks/useCrud';
import { useAsideContext } from '@contexts/AsideContext';
import { PageMeta, TableCard, ColumnDefinition } from '@common/components';
import { Button, Badge, ConfirmDialog, Input, StatTile } from '@common/components/ui';
import userService from '@services/userService';
import { User } from '@services/core/types';

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface UserFormProps {
  user: User | null;
  onSave: (data: UserFormData) => Promise<void>;
  isSaving: boolean;
}

const UserForm = ({ user, onSave, isSaving }: UserFormProps) => {
  const [formData, setFormData] = useState<UserFormData>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Information</h3>
        <div className="space-y-4">
          <Input
            label="First Name"
            required
            value={formData.firstName}
            onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
          />
          <Input
            label="Last Name"
            required
            value={formData.lastName}
            onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
          />
          <Input
            label="Email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
          />
          <Input
            label="Phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
          />
        </div>
      </div>

      <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="submit" loading={isSaving} fullWidth>
          {user ? 'Update User' : 'Create User'}
        </Button>
      </div>
    </form>
  );
};

const Users = () => {
  const { openAside } = useAsideContext();
  const { isOpen: isDeleteOpen, open: openDelete, close: closeDelete } = useModal();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const { handleCreate, handleEdit, handleDelete, isSubmitting } = useCrud<UserFormData, User>({
    queryKey: 'users',
    service: {
      create: (data) => userService.create(data),
      update: (id, data) => userService.update(id, data),
      remove: (id) => userService.remove(id),
    },
    messages: { created: 'User created successfully', updated: 'User updated successfully', deleted: 'User deleted successfully' },
  });

  // Fetch users
  const { data: usersData, isLoading, error } = useTableQuery({
    queryKey: ['users', currentPage, pageSize, searchQuery],
    fetchFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('limit', String(pageSize));
      if (searchQuery) params.set('q', searchQuery);
      return userService.getAll(params);
    },
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => userService.getStats(),
  });

  const openUserForm = (user: User | null) => {
    openAside(
      <UserForm
        user={user}
        isSaving={isSubmitting}
        onSave={async (data) => {
          if (user) {
            await handleEdit(user.id, data);
          } else {
            await handleCreate(data);
          }
          queryClient.invalidateQueries({ queryKey: ['user-stats'] });
        }}
      />,
      { title: user ? 'Edit User' : 'Add User' }
    );
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    openDelete();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const users = usersData?.data.users || [];
  const total = usersData?.data.total || 0;
  const totalPages = usersData?.data.totalPages || 1;

  const columns: ColumnDefinition<User>[] = [
    {
      name: 'name',
      title: 'Name',
      value: (user) => (
        <div className="font-medium text-gray-900 dark:text-white">
          {user.firstName} {user.lastName}
        </div>
      ),
    },
    { name: 'email', title: 'Email', value: (user) => user.email },
    { name: 'phone', title: 'Phone', value: (user) => user.phone || '-' },
    {
      name: 'status',
      title: 'Status',
      value: (user) => (user.emailVerified ? <Badge color="info">Verified</Badge> : null),
    },
    { name: 'joined', title: 'Joined', value: (user) => formatDate(user.createdAt) },
  ];

  return (
    <>
      <PageMeta
        title="Users"
        breadcrumbs={[{ label: 'Users' }]}
        action={
          <Button onClick={() => openUserForm(null)} icon={<PlusIcon className="h-5 w-5" />}>
            Add User
          </Button>
        }
      />

      <div className="space-y-6">
        {/* Stats */}
        {stats?.data && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile label="Total Users" value={stats.data.total} icon={<UsersIcon />} iconClassName="bg-brand-500" />
            <StatTile label="Active Users" value={stats.data.active} icon={<CheckBadgeIcon />} iconClassName="bg-green-500" />
            <StatTile label="Verified Users" value={stats.data.verified} icon={<ShieldCheckIcon />} iconClassName="bg-blue-500" />
            <StatTile label="New This Month" value={stats.data.newThisMonth} icon={<SparklesIcon />} iconClassName="bg-purple-500" />
          </div>
        )}

        <TableCard
          columns={columns}
          data={users}
          loading={isLoading}
          error={error ? 'Failed to load users' : undefined}
          emptyMessage="No users found"
          searchable
          searchPlaceholder="Search by name, email, or phone..."
          onSearch={(query) => {
            setSearchQuery(query);
            setCurrentPage(1);
          }}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={total}
          itemsPerPage={pageSize}
          onPageChange={setCurrentPage}
          onRowClick={openUserForm}
          rowActions={(user) => (
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openUserForm(user);
                }}
                className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(user);
                }}
                className="text-danger-600 hover:text-danger-900 dark:text-danger-400 dark:hover:text-danger-300"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        />
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          closeDelete();
          setUserToDelete(null);
        }}
        onConfirm={async () => {
          if (!userToDelete) return;
          await handleDelete(userToDelete.id);
          queryClient.invalidateQueries({ queryKey: ['user-stats'] });
          setUserToDelete(null);
        }}
        title="Delete User"
        description={`Are you sure you want to delete user "${userToDelete?.firstName} ${userToDelete?.lastName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        successMessage="User deleted"
      />
    </>
  );
};

export default Users;
